/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
/* eslint-disable no-console */
import * as Raven from 'raven-js';

import PouchDB from 'pouchdb';
import PouchFind from 'pouchdb-find';

import MathHelper from 'common/helpers/MathHelper';
import PatternHelper from 'common/helpers/PatternHelper';
import ThumbnailHelper from 'common/helpers/ThumbnailHelper';
import PatternStoreHelper, {
  patternsDbName,
  piecesDbName,
  pieceKeyRange,
  pieceDeletions,
  PATTERN_DOC_TYPE,
} from 'common/helpers/PatternStoreHelper';
import { PATTERN_MARGIN_SIZE } from 'common/constants/numbers';

import Auth from '../Auth';

window.PouchDB = PouchDB;
PouchDB.plugin(PouchFind);

const { host } = new URL(window.location.href);

// CouchDB is served from the same origin as the app (CloudFront in AWS, the Vite proxy locally).
const protocol = window.location.protocol.replace(':', '');
const remoteUrl = (name) => `${protocol}://${host}/couchdb/${name}`;

// While editing, the thumbnail on the pattern document is redrawn at most this often. Leaving the
// editor always redraws it. Without the limit every edit would resend the pattern document,
// which is mostly thumbnail, on top of the piece that changed.
const THUMBNAIL_INTERVAL_MS = 30 * 1000;

// How long to wait for CouchDB before carrying on with the local copy (for example when offline).
const NETWORK_WAIT_MS = 4000;

/** CouchDB checks the same login token the API issues. Sent with every request to CouchDB. */
function tokenFetch(url, options) {
  options.headers.set('Authorization', `Bearer ${Auth.getToken()}`);
  return PouchDB.fetch(url, options);
}

function remoteDb(name) {
  // skip_setup: the API creates the user's databases; the browser may not create databases.
  return new PouchDB(remoteUrl(name), { fetch: tokenFetch, skip_setup: true });
}

/**
 * The local copy lives in IndexedDB, so it survives reloads and holds edits made offline.
 * Auto-compaction drops old piece revisions, which every edit creates.
 */
function localDb(name) {
  return new PouchDB(name, { auto_compaction: true });
}

/** Resolves true when the promise succeeds, or false after `ms` or on failure. Never rejects. */
function within(ms, promise) {
  return Promise.race([
    Promise.resolve(promise).then(() => true, () => false),
    new Promise((resolve) => setTimeout(() => resolve(false), ms)),
  ]);
}

/**
 * Whether CouchDB refused the login token. Expired tokens get 401, other users' databases 403,
 * and tokens that are damaged or signed with another key get 400 ("Bad HMAC", "Malformed token").
 */
function isLoginError(error) {
  if (!error) return false;
  if (error.status === 401 || error.status === 403) return true;
  const text = `${error.reason || ''} ${error.message || ''}`;
  return error.status === 400 && /HMAC|token|signature/i.test(text);
}

/** A sync that CouchDB refuses cannot recover. Send the user to sign in; local edits are kept. */
function handleSyncError(error) {
  if (isLoginError(error)) {
    window.location.href = '/app/login';
    return;
  }
  if (error) {
    console.error(error);
    Raven.captureException(error);
  }
}

/**
 * Combines several PouchDB syncs into one object with the same `on(event, fn)` interface.
 * `change` events are only passed on for changes that came from CouchDB, because local
 * changes are already on screen.
 */
function combine(syncs) {
  syncs.forEach((sync) => {
    sync.on('denied', handleSyncError);
    sync.on('error', handleSyncError);
    // With retry on, a failed attempt pauses with the error instead of failing. Being offline
    // is expected and just retried; a refused login is not.
    sync.on('paused', (error) => { if (isLoginError(error)) handleSyncError(error); });
  });
  const combined = {
    on(event, handler) {
      syncs.forEach((sync) => sync.on(event, (info) => {
        if (event === 'change' && info && info.direction === 'push') return;
        handler(info);
      }));
      return combined;
    },
    cancel() {
      syncs.forEach((sync) => sync.cancel());
    },
  };
  return combined;
}

/**
 * A user's patterns, kept in this browser and synced with CouchDB.
 *
 * Each user has two databases (see common/helpers/PatternStoreHelper):
 *   user_<id>_patterns  one small document per pattern, used for the pattern list
 *   user_<id>_pieces    one document per piece
 *
 * Edits are written to the local copy in IndexedDB first and synced to CouchDB in the background,
 * so a reload or a lost connection does not lose them. The browser authenticates to CouchDB with
 * its login token, and can only open its own user's databases.
 *
 * In the editor (`documentName` set) only the open pattern syncs, and a save writes only the
 * pieces that changed.
 */
export default class Pouchdb {
  constructor({ databaseName, documentName = null }) {
    this.userId = databaseName;
    this.documentName = documentName;
    this.pattern = null;
    // Set once the whole pattern has been loaded. Saving before that could delete pieces that
    // have not arrived yet, so savePattern refuses until then.
    this.loaded = false;
    this.lastThumbnailAt = 0;
    this.thumbnailStale = false;

    this.patternsRemote = remoteDb(patternsDbName(this.userId));
    this.piecesRemote = remoteDb(piecesDbName(this.userId));
    this.patternsLocal = localDb(patternsDbName(this.userId));
    this.piecesLocal = localDb(piecesDbName(this.userId));
  }

  /** Sync options that limit sync to the open pattern. */
  patternFilter() {
    return { doc_ids: [this.documentName] };
  }

  piecesFilter() {
    return { selector: { pattern_id: this.documentName } };
  }

  /**
   * Starts two-way live sync with CouchDB. In the editor only the open pattern syncs. On the home
   * page all pattern documents sync, and any piece edits still waiting from earlier are sent.
   * Returns an object with `on('change' | 'error', fn)`.
   */
  startReplicationFromRemote() {
    const live = { live: true, retry: true };
    if (!this.documentName) {
      return combine([
        PouchDB.sync(this.patternsLocal, this.patternsRemote, live),
        PouchDB.replicate(this.piecesLocal, this.piecesRemote, live),
      ]);
    }
    return combine([
      PouchDB.sync(this.patternsLocal, this.patternsRemote, { ...live, ...this.patternFilter() }),
      PouchDB.sync(this.piecesLocal, this.piecesRemote, { ...live, ...this.piecesFilter() }),
    ]);
  }

  /**
   * Loads the open pattern. Fetches the latest from CouchDB first if it answers quickly, then reads
   * the local copy, so a pattern opens offline if this browser has it. Returns null if the pattern
   * is not complete yet; the next sync change will complete it.
   */
  async saveDataFromRemoteDB() {
    await within(NETWORK_WAIT_MS, Promise.all([
      this.patternsLocal.replicate.from(this.patternsRemote, this.patternFilter()),
      this.piecesLocal.replicate.from(this.piecesRemote, this.piecesFilter()),
    ]));
    return this.getDataFromLocalDB();
  }

  async readLocalPieces() {
    const { rows } = await this.piecesLocal.allDocs({ include_docs: true, ...pieceKeyRange(this.documentName) });
    return rows.map((row) => row.doc);
  }

  /**
   * The open pattern, joined back together from its local documents.
   * Returns null while some of its pieces are still syncing; the next change event will try again.
   */
  async getDataFromLocalDB() {
    const patternDoc = await this.patternsLocal.get(this.documentName);
    const pieceDocs = await this.readLocalPieces();
    if (PatternStoreHelper.missingPieces(patternDoc, pieceDocs).length) return null;
    const pattern = PatternStoreHelper.join(patternDoc, pieceDocs);
    this.pattern = pattern;
    if (!this.loaded) {
      // The stored thumbnail is current as of opening, so the interval starts now.
      this.lastThumbnailAt = Date.now();
    }
    this.loaded = true;
    this.data = { ...patternDoc, pattern };
    return this.data;
  }

  /**
   * Sends anything not yet in CouchDB, waiting a few seconds at most. Used before leaving a page.
   * Returns false if it could not finish (for example offline); the edits stay in the local copy
   * and go the next time the app syncs.
   */
  async flush() {
    const pushes = [PouchDB.replicate(this.patternsLocal, this.patternsRemote)];
    pushes.push(this.documentName
      ? PouchDB.replicate(this.piecesLocal, this.piecesRemote, this.piecesFilter())
      : PouchDB.replicate(this.piecesLocal, this.piecesRemote));
    return within(NETWORK_WAIT_MS, Promise.all(pushes));
  }

  /**
   * Saves the open pattern to the local copy, writing only documents that changed:
   *   - the pieces that were edited, added or removed;
   *   - the pattern document when its title, settings, texts or piece list changed, or when the
   *     thumbnail is due to be redrawn.
   * Live sync sends them to CouchDB. Pass { final: true } when leaving the editor, so the thumbnail
   * is brought up to date and the changes are sent before the page goes.
   */
  async savePattern({ final = false } = {}) {
    if (!this.pattern || !this.loaded) return null;
    // Finished rulers are part of the pattern. A ruler with only a start point is still being placed.
    if (this.pattern.ruler) {
      const ruler = {};
      Object.keys(this.pattern.ruler).forEach((rulerId) => {
        const entry = this.pattern.ruler[rulerId];
        if (entry && entry.endPoint) ruler[rulerId] = entry;
      });
      if (Object.keys(ruler).length) this.pattern.ruler = ruler;
      else delete this.pattern.ruler;
    }
    const id = this.documentName;

    let stored = null;
    try {
      stored = await this.patternsLocal.get(id);
    } catch (error) {
      if (error.status !== 404) throw error;
    }

    const diff = PatternStoreHelper.diff(id, this.pattern, stored, await this.readLocalPieces());
    if (diff.piecesChanged) this.thumbnailStale = true;

    const hasThumbnail = stored && ThumbnailHelper.isStoredThumbnail(stored.thumbnail);
    const thumbnailDue = this.thumbnailStale
      && (final || !hasThumbnail || Date.now() - this.lastThumbnailAt >= THUMBNAIL_INTERVAL_MS);
    const writePatternDoc = !stored || diff.patternChanged || thumbnailDue;

    const pieceWrites = [...diff.upserts, ...diff.deletes];
    if (pieceWrites.length) {
      await this.piecesLocal.bulkDocs(pieceWrites);
    }

    if (writePatternDoc) {
      const now = new Date().toISOString();
      const base = diff.patternDoc || stored;
      const patternDoc = { ...base, modified_date: now };
      if (!stored) patternDoc.created_date = now;
      if (thumbnailDue || !ThumbnailHelper.isStoredThumbnail(patternDoc.thumbnail)) {
        // Thumbnails are drawn here in the browser and sync with the pattern document.
        patternDoc.thumbnail = ThumbnailHelper.createThumbnailDataUrl(this.pattern);
        this.lastThumbnailAt = Date.now();
        this.thumbnailStale = false;
      }
      await this.patternsLocal.put(patternDoc);
    }

    if (final) await this.flush();
    return stored;
  }

  loadData(viewportSize) {
    if (!viewportSize) return null;
    const margin = { x: PATTERN_MARGIN_SIZE, y: PATTERN_MARGIN_SIZE };
    const patternBounds = PatternHelper.getBounds(this.pattern);
    const { scale, offset } = PatternHelper.getOffsetAndScaleToFitBounds(patternBounds, viewportSize, margin);
    const scaledPatternSize = MathHelper.scalarMultiplication(this.pattern, scale);

    return {
      scale,
      offset,
      viewportSize,
      scaledPatternSize,
    };
  }

  /**
   * The user's patterns for the home page, read from the local pattern documents after a quick
   * sync. A pattern without a thumbnail (one the API created) gets one drawn now and saved, once.
   */
  async getPatterns() {
    await within(NETWORK_WAIT_MS, PouchDB.sync(this.patternsLocal, this.patternsRemote));
    const { rows } = await this.patternsLocal.allDocs({ include_docs: true });
    const patternDocs = rows
      .map((row) => row.doc)
      .filter((doc) => doc && doc.type === PATTERN_DOC_TYPE);

    const list = [];
    for (const doc of patternDocs) { // eslint-disable-line no-restricted-syntax
      let { thumbnail } = doc;
      if (!ThumbnailHelper.isStoredThumbnail(thumbnail)) {
        thumbnail = await this.drawMissingThumbnail(doc); // eslint-disable-line no-await-in-loop
      }
      list.push({ id: doc._id, title: doc.pattern.title, thumbnail });
    }
    return list;
  }

  async drawMissingThumbnail(patternDoc) {
    try {
      const { rows } = await this.piecesRemote.allDocs({ include_docs: true, ...pieceKeyRange(patternDoc._id) });
      const pattern = PatternStoreHelper.join(patternDoc, rows.map((row) => row.doc));
      const thumbnail = ThumbnailHelper.createThumbnailDataUrl(pattern);
      await this.patternsLocal.put({ ...patternDoc, thumbnail });
      return thumbnail;
    } catch (error) {
      // Offline, or the pieces are not reachable: show a blank thumbnail for now.
      return ThumbnailHelper.createThumbnailDataUrl({ pieces: {} });
    }
  }

  /**
   * Deletes a pattern: its pattern document first, so it leaves the list, then its pieces.
   * Works offline for whatever this browser has; the deletions sync later.
   */
  async deletePattern(patternId) {
    const doc = await this.patternsLocal.get(patternId);
    await this.patternsLocal.remove(doc);

    const local = await this.piecesLocal.allDocs(pieceKeyRange(patternId));
    if (local.rows.length) {
      await this.piecesLocal.bulkDocs(pieceDeletions(patternId, local.rows));
    }
    // Pieces this browser never downloaded can only be deleted in CouchDB directly.
    try {
      const localIds = new Set(local.rows.map((row) => row.id));
      const remote = await this.piecesRemote.allDocs(pieceKeyRange(patternId));
      const onlyRemote = remote.rows.filter((row) => !localIds.has(row.id));
      if (onlyRemote.length) {
        await this.piecesRemote.bulkDocs(pieceDeletions(patternId, onlyRemote));
      }
    } catch (error) {
      console.warn('Could not reach CouchDB to delete pieces of', patternId, error);
    }
    await this.flush();
  }

  /**
   * Called when signing out: sends anything still waiting, then removes this user's local copy
   * from the browser. If something could not be sent (offline), the local copy is kept so no
   * edits are lost; it will sync the next time this user signs in here.
   */
  static async signOut(userId) {
    if (!userId) return;
    const store = new Pouchdb({ databaseName: userId });
    const sent = await store.flush();
    if (sent) {
      await Promise.all([store.patternsLocal.destroy(), store.piecesLocal.destroy()]).catch(() => {});
    }
  }

  handlingErrors(error) {
    console.error(error);
    Raven.captureException(error);
  }
}
